import gsap from 'gsap';

export default function initMobileMenu() {
  const burger = document.querySelector('.nav-opener');
  const navMenu = document.querySelector('#main-menu');

  if (!burger || !navMenu) return;

  const menuHolder = navMenu.querySelector('.menu-holder');
  const navList = navMenu.querySelector('.nav-list');
  const mq = window.matchMedia('(min-width: 1024px)');
  const classOpen = 'nav-active';

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null);
  }

  function setupMenu() {
    if (mq.matches) {
      gsap.set(navMenu, { clearProps: 'height,overflow' });
      navMenu.removeAttribute('aria-hidden');
      navMenu.removeAttribute('inert');
      if (document.body.classList.contains(classOpen)) {
        document.body.classList.remove(classOpen);
        burger.setAttribute('aria-expanded', 'false');
        burger.setAttribute('aria-label', 'Open navigation menu');
      }
    } else {
      if (!document.body.classList.contains(classOpen)) {
        gsap.set(navMenu, { height: 0, overflow: 'hidden' });
        navMenu.setAttribute('aria-hidden', 'true');
        navMenu.setAttribute('inert', '');
      }
    }
  }

  function openMenu() {
    document.body.classList.add(classOpen);
    burger.setAttribute('aria-expanded', 'true');
    burger.setAttribute('aria-label', 'Close navigation menu');
    navMenu.removeAttribute('aria-hidden');
    navMenu.removeAttribute('inert');
    gsap.killTweensOf(navMenu);
    gsap.fromTo(navMenu,
      { height: 0, overflow: 'hidden' },
      { height: 'auto', duration: 0.35 }
    );
  }

  function closeMenu() {
    navMenu.querySelectorAll('.nav-item.sub-active').forEach(active => {
      const el = active.querySelector('.nav-drop');
      if (el) {
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('inert', '');
        gsap.set(el, { x: '100%' });
        active.classList.remove('sub-active');
      }
    });
    if (menuHolder) gsap.set(menuHolder, { clearProps: 'height' });

    gsap.killTweensOf(navMenu);
    document.body.classList.remove(classOpen);
    gsap.set(navMenu, { height: navMenu.scrollHeight, overflow: 'hidden' });
    gsap.to(navMenu, { height: 0, duration: 0.35, onComplete() {
      burger.setAttribute('aria-expanded', 'false');
      burger.setAttribute('aria-label', 'Open navigation menu');
      navMenu.setAttribute('aria-hidden', 'true');
      navMenu.setAttribute('inert', '');
    }});
  }

  function openSubDesktop(item, drop) {
    navMenu.querySelectorAll('.nav-item.sub-active').forEach(active => {
      if (active !== item) {
        const el = active.querySelector('.nav-drop');
        if (el) closeSubDesktop(active, el);
      }
    });
    item.classList.add('sub-active');
    drop.removeAttribute('aria-hidden');
    drop.removeAttribute('inert');
    const focusable = getFocusable(drop);
    if (focusable.length) focusable[0].focus();
  }

  function closeSubDesktop(item, drop) {
    item.classList.remove('sub-active');
    drop.setAttribute('aria-hidden', 'true');
    drop.setAttribute('inert', '');
  }

  function openSub(item, drop) {
    navMenu.querySelectorAll('.nav-item.sub-active').forEach(active => {
      if (active !== item) {
        const el = active.querySelector('.nav-drop');
        if (el) closeSub(active, el);
      }
    });

    item.classList.add('sub-active');
    drop.removeAttribute('aria-hidden');
    drop.removeAttribute('inert');

    gsap.to(menuHolder, { height: drop.offsetHeight, duration: 0.2 });
    gsap.to(drop, { x: 0, onComplete() {
      const focusable = getFocusable(drop);
      if (focusable.length) focusable[0].focus();
    }});
  }

  function closeSub(item, drop) {
    gsap.to(menuHolder, { height: navList.offsetHeight, duration: 0.2, delay: 0.15, onComplete() {
      gsap.set(menuHolder, { clearProps: 'height' });
    }});
    gsap.to(drop, { x: '100%', onComplete() {
      drop.setAttribute('aria-hidden', 'true');
      drop.setAttribute('inert', '');
      item.classList.remove('sub-active');
    }});
  }

  if (!menuHolder || !navList) return;

  setupMenu();
  mq.addEventListener('change', setupMenu);

  burger.addEventListener('click', () => {
    document.body.classList.contains(classOpen) ? closeMenu() : openMenu();
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const activeSub = navMenu.querySelector('.nav-item.sub-active');
    if (activeSub) {
      const el = activeSub.querySelector('.nav-drop');
      if (el) {
        mq.matches ? closeSubDesktop(activeSub, el) : closeSub(activeSub, el);
        activeSub.querySelector('.nav-link').focus();
      }
    } else if (document.body.classList.contains(classOpen)) {
      closeMenu();
      burger.focus();
    }
  });

  navMenu.addEventListener('keydown', e => {
    if (e.key !== 'Tab' || e.shiftKey || mq.matches) return;
    if (!document.body.classList.contains(classOpen)) return;
    if (navMenu.querySelector('.nav-item.sub-active')) return;

    const focusable = getFocusable(navMenu);
    if (focusable.length && focusable[focusable.length - 1] === document.activeElement) {
      e.preventDefault();
      burger.focus();
    }
  });

  navMenu.querySelectorAll('.nav-item.has-child').forEach(item => {
    const link = item.querySelector('.nav-link');
    const drop = item.querySelector('.nav-drop');
    const back = drop ? drop.querySelector('[data-nav-back]') : null;
    if (!link || !drop) return;

    function setupDrop() {
      if (mq.matches) {
        gsap.set([drop, menuHolder, navList], { clearProps: 'all' });
        item.classList.remove('sub-active');
        drop.setAttribute('aria-hidden', 'true');
        drop.setAttribute('inert', '');
      } else {
        if (!item.classList.contains('sub-active')) {
          drop.setAttribute('aria-hidden', 'true');
          drop.setAttribute('inert', '');
          gsap.set(drop, { x: '100%' });
        }
      }
    }

    setupDrop();
    mq.addEventListener('change', setupDrop);

    link.addEventListener('click', e => {
      if (mq.matches) return;
      e.preventDefault();
      openSub(item, drop);
    });

    link.addEventListener('keydown', e => {
      if (!mq.matches) return;
      if (e.key !== 'Enter' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      openSubDesktop(item, drop);
    });

    item.addEventListener('mouseenter', () => {
      if (!mq.matches) return;
      drop.removeAttribute('aria-hidden');
      drop.removeAttribute('inert');
    });

    item.addEventListener('mouseleave', () => {
      if (!mq.matches) return;
      if (!item.classList.contains('sub-active')) {
        drop.setAttribute('aria-hidden', 'true');
        drop.setAttribute('inert', '');
      }
    });

    item.addEventListener('focusout', e => {
      if (!mq.matches) return;
      if (!item.contains(e.relatedTarget)) {
        closeSubDesktop(item, drop);
      }
    });

    if (back) {
      back.addEventListener('click', () => {
        closeSub(item, drop);
        link.focus();
      });
    }

    drop.addEventListener('keydown', e => {
      if (mq.matches) {
        if (e.key === 'Escape') {
          closeSubDesktop(item, drop);
          link.focus();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(drop);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  });
}
